import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../services/axiosInstance';
import { repAxios, isRepresentativeAuthenticated } from '../services/representativeService';

/**
 * /t/:ref — link universale del ticket (bottone «Condividi»). `ref` e il numero
 * (2026-000049) o l'id Mongo. Nessun token nel link: decide la sessione gia
 * aperta in questo browser.
 *
 * - Utente admin collegato → resolve nel suo perimetro → /admin/centro-ticket?ticket=<id>.
 * - Rappresentante collegato (representative_token valido) → /area-associativa/supporto/<id>.
 * - Nessuna sessione → scelta dell'accesso, con ?next=/t/<ref> (i login lo
 *   accettano solo tramite safeNext: percorsi interni).
 * - 403/404 → «Non hai accesso a questo ticket o non esiste».
 * - 409 (numero ambiguo) vince su 403/404 dell'altra sessione: e l'indicazione utile.
 * L'associato non e previsto: il link e degli operatori. Con la sola sessione
 * da associato la pagina lo dice, invece di riproporre gli accessi a vuoto.
 * Prima del resolve la pagina non mostra alcun dato del ticket.
 */
type Area = 'admin' | 'representative';
type State =
  | { kind: 'loading' }
  | { kind: 'login' }
  | { kind: 'associate' }
  | { kind: 'denied'; areas: Area[] }
  | { kind: 'error'; message: string; areas: Area[] };

const AREA_LINKS: Record<Area, { to: string; label: string }> = {
  admin: { to: '/admin/centro-ticket', label: 'Vai al Centro ticket' },
  representative: { to: '/area-associativa/supporto', label: 'Vai al supporto dell\'area associativa' },
};

const statusOf = (e: unknown): number | undefined => (e as { response?: { status?: number } })?.response?.status;
const messageOf = (e: unknown): string | undefined => (e as { response?: { data?: { error?: string } } })?.response?.data?.error;

/** Sessione da associato ancora valida (sola lettura del token: nessun effetto collaterale). */
const hasAssociateSession = (): boolean => {
  try {
    const token = localStorage.getItem('associate_token');
    if (!token) return false;
    const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return !payload?.exp || payload.exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

const resolveAs = async (area: Area, ref: string): Promise<string> => {
  const path = area === 'admin'
    ? `/admin/support-tickets/resolve/${encodeURIComponent(ref)}`
    : `/representative/support/tickets/resolve/${encodeURIComponent(ref)}`;
  const res = await (area === 'admin' ? axiosInstance : repAxios).get(path);
  const id = res?.data?.data?.id;
  if (typeof id !== 'string' || !/^[a-f\d]{24}$/i.test(id)) throw new Error('Risposta non valida');
  return id;
};

const targetFor = (area: Area, id: string): string =>
  area === 'admin' ? `/admin/centro-ticket?ticket=${id}` : `/area-associativa/supporto/${id}`;

const TicketLinkPage: React.FC = () => {
  const { ref = '' } = useParams<{ ref: string }>();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [state, setState] = useState<State>({ kind: 'loading' });
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current || loading) return;
    startedRef.current = true;
    const areas: Area[] = [];
    if (user) areas.push('admin');
    if (isRepresentativeAuthenticated()) areas.push('representative');
    if (!areas.length) { setState({ kind: hasAssociateSession() ? 'associate' : 'login' }); return; }

    (async () => {
      let denied = false;
      let ambiguous: string | null = null;
      let failure: string | null = null;
      // In ordine: admin, poi rappresentante (profilo doppio: se il primo non vede il ticket prova l'altro).
      for (const area of areas) {
        try {
          const id = await resolveAs(area, ref);
          navigate(targetFor(area, id), { replace: true });
          return;
        } catch (e) {
          const st = statusOf(e);
          if (st === 403 || st === 404) denied = true;
          else if (st === 409) ambiguous = messageOf(e) || 'Numero ambiguo: indica anche l\'anno.';
          else if (!failure) failure = 'Non è stato possibile aprire il ticket.';
        }
      }
      if (ambiguous) setState({ kind: 'error', message: ambiguous, areas });
      else if (failure && !denied) setState({ kind: 'error', message: failure, areas });
      else setState({ kind: 'denied', areas });
    })();
  }, [loading, user, ref, navigate]);

  const next = `/t/${encodeURIComponent(ref)}`;
  const areaLinks = (areas: Area[]) => (
    <div className="mt-4 flex flex-col items-center gap-2 text-sm">
      {areas.map((a) => (
        <Link key={a} to={AREA_LINKS[a].to} className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
          {AREA_LINKS[a].label}
        </Link>
      ))}
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Helmet>
        <meta name="robots" content="noindex" />
        <title>Apertura ticket - UNASCA</title>
      </Helmet>
      <div className="max-w-sm w-full text-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        {state.kind === 'loading' && (
          <p className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Apertura del ticket…
          </p>
        )}
        {state.kind === 'login' && (
          <>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Accedi per aprire il ticket</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Il link è riservato a chi gestisce i ticket.</p>
            <div className="mt-4 flex flex-col gap-2">
              <Link
                to={`/admin/login?next=${encodeURIComponent(next)}`}
                className="inline-flex justify-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                Accedi come amministratore
              </Link>
              <Link
                to={`/accesso-associati?next=${encodeURIComponent(next)}`}
                className="inline-flex justify-center rounded-lg border border-gray-300 dark:border-gray-600 px-4 py-2 text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Accedi come rappresentante
              </Link>
            </div>
          </>
        )}
        {state.kind === 'associate' && (
          <>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Questo link è per amministratori e rappresentanti del canale</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
              Sei collegato come associato: le tue richieste di supporto sono nella tua area riservata.
            </p>
            <div className="mt-4 flex flex-col items-center gap-2 text-sm">
              <Link to="/area-riservata" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">
                Vai all'area riservata
              </Link>
            </div>
          </>
        )}
        {state.kind === 'denied' && (
          <>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Non hai accesso a questo ticket o non esiste</h1>
            {areaLinks(state.areas)}
          </>
        )}
        {state.kind === 'error' && (
          <>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">{state.message}</h1>
            {areaLinks(state.areas)}
          </>
        )}
      </div>
    </div>
  );
};

export default TicketLinkPage;
